import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];
const EASE_MASK = [0.76, 0, 0.24, 1];

/* Scroll-triggered fade/slide reveal. */
export function Reveal({ children, delay = 0, y = 42, className = "", once = true, ...rest }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once, margin: "0px 0px -12% 0px" }}
            transition={{ duration: 0.95, ease: EASE, delay }}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

/*
 * Masked, line-by-line headline reveal. Pass an array of lines. Each line sits
 * in an overflow-hidden track; the inner span slides up from 110%.
 * `trigger` = "load" (animate on mount, for hero) or "view" (whileInView).
 */
export function MaskedLines({
    lines,
    trigger = "load",
    className = "",
    lineClassName = "",
    stagger = 0.12,
    delay = 0.15,
    as: Tag = "h1",
}) {
    const parent = {
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
    };
    const child = {
        hidden: { y: "115%" },
        show: { y: "0%", transition: { duration: 1.1, ease: EASE_MASK } },
    };
    const viewProps =
        trigger === "load"
            ? { initial: "hidden", animate: "show" }
            : { initial: "hidden", whileInView: "show", viewport: { once: true, margin: "0px 0px -10% 0px" } };

    return (
        <Tag className={className}>
            <motion.span variants={parent} {...viewProps} className="block">
                {lines.map((line, i) => (
                    <span key={i} className="block overflow-hidden pb-[0.08em]">
                        <motion.span variants={child} className={`block ${lineClassName}`}>
                            {line}
                        </motion.span>
                    </span>
                ))}
            </motion.span>
        </Tag>
    );
}

/* Vertical parallax wrapper driven by scroll position. */
export function Parallax({ children, distance = 120, className = "" }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [-distance / 2, distance / 2]);
    return (
        <motion.div ref={ref} style={{ y }} className={className}>
            {children}
        </motion.div>
    );
}

/*
 * Slow, seamless editorial marquee. Duplicates content for an infinite loop.
 */
export function Marquee({ items, speed = 42, className = "", reverse = false, testid }) {
    const list = [...items, ...items];
    return (
        <div className={`overflow-hidden ${className}`} data-testid={testid}>
            <div
                className="flex whitespace-nowrap will-change-transform"
                style={{
                    animation: `marquee ${speed}s linear infinite`,
                    animationDirection: reverse ? "reverse" : "normal",
                }}
            >
                {list.map((it, i) => (
                    <span key={i} className="flex items-center shrink-0">
                        {it}
                    </span>
                ))}
            </div>
        </div>
    );
}
